import styled, { css } from 'styled-components'
import { colors, fieldLabel, fontScope, radius } from '../tokens'

export const Wrapper = styled.section`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

export const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${colors.ink900};
`

export const Scroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.tableBorder};
  border-radius: ${radius.table};
  background: ${colors.white};

  @media print {
    overflow: visible;
    border-radius: 0;
  }
`

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.ink700};
`

const sticky = css<{ $sticky?: boolean }>`
  ${({ $sticky }) =>
    $sticky === true &&
    css`
      position: sticky;
      left: 0;
      z-index: 1;

      @media print {
        position: static;
      }
    `}
`

const align = css<{ $numeric?: boolean }>`
  text-align: ${({ $numeric }) => ($numeric === true ? 'right' : 'left')};
`

export const HeadCell = styled.th<{ $numeric?: boolean, $sticky?: boolean }>`
  ${fieldLabel}
  ${align}
  ${sticky}
  padding: 12px 16px;
  white-space: nowrap;
  background: ${colors.accent};
  border-bottom: 1px solid ${colors.tableBorder};
`

export const Row = styled.tr<{ $open: boolean }>`
  & > th,
  & > td {
    background: ${({ $open }) => ($open ? colors.zebra : colors.white)};
  }
`

export const RowHead = styled.th<{ $sticky?: boolean }>`
  ${sticky}
  padding: 10px 16px;
  text-align: left;
  font-weight: 600;
  color: ${colors.ink900};
  border-bottom: 1px solid ${colors.rowDivider};
  background: ${colors.white};
  min-width: 200px;
`

export const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  width: 12px;
  font-size: 18px;
  line-height: 1;
  color: ${colors.ink500};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;

  @media print {
    display: none;
  }
`

export const Cell = styled.td<{ $numeric?: boolean, $strong?: boolean }>`
  ${align}
  padding: 10px 16px;
  white-space: nowrap;
  vertical-align: top;
  border-bottom: 1px solid ${colors.rowDivider};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $strong }) => ($strong === true ? 700 : 400)};
`

export const Quantity = styled.span`
  display: block;
  font-weight: 600;
  color: ${colors.ink900};
`

export const Money = styled.span`
  display: block;
  font-size: 12px;
  line-height: 18px;
  color: ${colors.ink500};
`

export const Empty = styled.span`
  color: ${colors.ink400};
`

export const DetailCell = styled.td`
  padding: 12px 16px 16px;
  background: ${colors.zebra};
  border-bottom: 1px solid ${colors.tableBorder};
`

export const Details = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: ${colors.ink600};
`

export const TotalRow = styled.tr`
  & > th,
  & > td {
    border-bottom: 0;
    background: ${colors.accent};
    font-weight: 700;
    color: ${colors.ink900};
  }
`

export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const card = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.input};
  background: ${colors.white};
`

export const Card = styled.section`
  ${card}
`

export const TotalCard = styled.section`
  ${card}
  background: ${colors.totalBar};
  border-color: ${colors.totalBar};

  & h4,
  & p {
    color: ${colors.white};
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`

export const CardTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${colors.ink900};
  overflow-wrap: anywhere;
`

export const CardTotal = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
  color: ${colors.ink900};
`

export const FormList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 12px;
  margin: 0;
`

export const FormItem = styled.div`
  display: contents;
`

export const FormLabel = styled.dt`
  font-size: 12px;
  line-height: 18px;
  color: ${colors.ink600};
`

export const FormValue = styled.dd`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-align: right;
  color: ${colors.ink900};
  font-variant-numeric: tabular-nums;
`

export const CardToggle = styled.button`
  align-self: flex-start;
  padding: 4px 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primaryStrong};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`
